import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProducts } from "@/hooks/useProducts";
import { useStore } from "@/hooks/useStore";
import { Button } from "@/components/ui/button";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { ProductForm } from "@/components/products/ProductForm";
import { ProductFormData } from "@/types/product";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export default function NewProduct() {
  const navigate = useNavigate();
  const { user, isFlorist, loading: authLoading } = useAuth();
  const [storeId, setStoreId] = useState<string | null>(null);
  const { createProduct: createProductMutation, isLoading: isLoadingProductsList } = useProducts({ storeId: storeId || "" });
  const { data: store, isLoading: storeLoading } = useStore();
  
  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    if (!isFlorist) {
      navigate("/");
      return;
    }
  }, [user, isFlorist, authLoading, navigate]);

  useEffect(() => {
    if (authLoading || !user) {
      return;
    }
    const fetchStoreId = async () => {
      try {
        const { data, error } = await supabase
          .from("store_profiles")
          .select("id")
          .eq("florist_id", user.id)
          .single();

        if (error) {
          console.error("Error fetching store:", error.message);
          toast.error("Failed to fetch store information");
          return;
        }

        if (data) {
          setStoreId(data.id);
        } else {
          // No store found for this user
          toast.error("No store found for your account. Please create a store first.");
          navigate("/store-settings");
        }
      } catch (error) {
        console.error("Unexpected error:", error);
        toast.error("An unexpected error occurred");
      }
    };

    fetchStoreId();
  }, [user, navigate]);

  if (!user || !isFlorist) {
    return null;
  }

  if (storeLoading || (isLoadingProductsList && !storeId)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-gray-900">Store Not Found</h1>
          <p className="mt-2 text-gray-600">
            Please complete your store profile before adding products.
          </p>
          <Button
            onClick={() => navigate("/store-settings")}
            className="mt-4"
          >
            Set Up Store
          </Button>
        </div>
      </div>
    );
  }

  const handleProductCreate = async (data: ProductFormData) => {
    try {
      // Image upload is optional - products can be created without images

      console.log("🔍 DEBUG: Form occasions data:", data.occasions);
      
      const { data: occasionData, error: occasionError } = await supabase
        .from('occasions')
        .select('id, name')
        .in('name', data.occasions);

      console.log("🔍 DEBUG: Database occasion lookup result:", occasionData);
      console.log("🔍 DEBUG: Occasion lookup error:", occasionError);

      if (occasionError) throw occasionError;

      const transformedData = {
        title: data.title,
        description: data.description,
        category: data.category,
        active: data.active,
        image_url: data.sizes[0]?.image_url,
        gallery_image_urls: data.gallery_image_urls || [],
        occasions: occasionData || [],
        sizes: data.sizes.map(size => ({
          size_name: size.size_name,
          price: size.price,
          image_url: size.use_product_image ? data.sizes[0]?.image_url : size.image_url,
          use_product_image: size.use_product_image
        }))
      };

      const createdProduct = await createProductMutation.mutateAsync(transformedData);
      
      // Navigate to the florist's product list page after creation
      if (createdProduct?.id) {
        navigate("/florist-dashboard/products");
      } else {
        toast.error("Failed to create product. Please try again.");
      }
    } catch (error: any) {
      // Error toast is now handled by the mutation's onError
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Button
          onClick={() => navigate("/florist-dashboard/products")}
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Products
        </Button>
      </div>

      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Create New Product</h1>
        <ProductForm 
          onSubmit={handleProductCreate} 
          isLoading={createProductMutation.isPending || storeLoading}
          initialData={{
            active: true,
            occasions: [],
            sizes: [{
              size_name: "Standard",
              price: 0,
              image_url: null,
              use_product_image: true
            }]
          }}
        />
      </div>
    </div>
  );
} 